import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import UndoIcon from '@mui/icons-material/Undo';
import ArchiveIcon from '@mui/icons-material/Archive';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MAX_EPISODES = 24;

function EpisodeChip({ anime }) {
  const episodes = getEpisodeCount(anime);
  const tooLong = episodes != null && episodes > MAX_EPISODES;

  return (
    <Chip
      size="small"
      label={episodeLabel(anime)}
      sx={{
        height: 22,
        borderRadius: 999,
        backgroundColor: tooLong ? '#fff5f5' : '#f0fdf4',
        border: tooLong ? '1px solid #fecaca' : '1px solid #bbf7d0',
        color: tooLong ? '#b91c1c' : '#166534',
        fontWeight: 700,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function getEpisodeCount(anime) {
      const episodes = Number(anime?.episodes);
      return anime?.episodes == null || !Number.isFinite(episodes) ? null : episodes;
}

function episodeLabel(anime) {
      const episodes = getEpisodeCount(anime);
      return episodes == null ? 'Episodes unknown' : `${episodes} ${plural(episodes, 'ep')}`;
}

function animeTitle(anime) {
  return anime?.title_english || anime?.title || 'Untitled anime';
}

function plural(value, one, many = `${one}s`) {
  return value === 1 ? one : many;
}

function getGenres(anime) {
  return Array.isArray(anime?.genres) ? anime.genres.slice(0, 2) : [];
}

function sortItems(items) {
  return (items || []).slice().sort((a, b) => {
    const votes = (b.votes || 0) - (a.votes || 0);
    if (votes !== 0) return votes;
    return new Date(a.added_at || 0) - new Date(b.added_at || 0);
  });
}

function countdownParts(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function formatSydneyDate(isoString) {
  if (!isoString) return '';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(isoString));
  } catch (error) {
    return isoString;
  }
}

function CountdownTile({ label, value }) {
  return (
    <Box sx={{ minWidth: { xs: 40, sm: 78 }, px: 1.5, py: 1.25, border: '1px solid #e5e7eb', borderRadius: 3, backgroundColor: '#fff', textAlign: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>{String(value).padStart(2, '0')}</Typography>
      <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 800 }}>{label}</Typography>
    </Box>
  );
}

function Poster({ anime, compact = false }) {
  const width = compact ? 46 : 54;
  const height = compact ? 66 : 76;
  return anime?.image_url ? (
    <Box component="img" src={anime.image_url} alt={animeTitle(anime)} loading="lazy" sx={{ width, height, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, backgroundColor: '#f4f4f5' }} />
  ) : (
    <Box sx={{ width, height, borderRadius: 1.5, flexShrink: 0, backgroundColor: '#f4f4f5', color: '#a1a1aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
      No image
    </Box>
  );
}

function AnimeMeta({ anime, showEpisodes = true }) {
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
      {showEpisodes ? <EpisodeChip anime={anime} /> : null}

      <Chip
        size="small"
        label={anime?.year || 'Year unknown'}
        sx={{
          height: 22,
          borderRadius: 999,
          backgroundColor: '#fafafa',
          border: '1px solid #e4e4e7',
          color: '#52525b',
          fontWeight: 600,
          '& .MuiChip-label': { px: 1 },
        }}
      />

      {getGenres(anime).map((genre) => (
        <Chip
          key={genre}
          size="small"
          label={genre}
          sx={{
            height: 22,
            borderRadius: 999,
            backgroundColor: '#eef2ff',
            border: '1px solid #e0e7ff',
            color: '#4338ca',
            fontWeight: 600,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ))}
    </Stack>
  );
}

function QuotaBar({ limits }) {
  const limit = Number(limits?.max_suggestions_per_ip ?? limits?.limit ?? 6);
  const used = Number(limits?.suggestions_used_by_you ?? limits?.used ?? 0);
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 6;
  const safeUsed = Number.isFinite(used) ? Math.max(0, used) : 0;
  const remaining = Math.max(0, safeLimit - safeUsed);
  const percent = Math.min(100, (safeUsed / safeLimit) * 100);

  return (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: 2.5, border: '1px solid #e4e4e7', backgroundColor: '#fafafa' }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
        <Typography variant="caption" sx={{ color: '#52525b', fontWeight: 850 }}>{safeUsed} / {safeLimit} suggestions used</Typography>
        <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 750 }}>{remaining} remaining</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 999, backgroundColor: '#e4e4e7', '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: '#111827' } }} />
    </Box>
  );
}

function MalLink({ anime, children, sx }) {
  if (!anime?.url) return children;

  return (
    <Box
      component="a"
      href={anime.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function PollRow({ anime, onToggleVote, loadingId, disabled }) {
  const voted = Boolean(anime.voted_by_you);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.25, sm: 1.75 }, py: 1, minHeight: 88, borderBottom: '1px solid #eef2f7', '&:last-of-type': { borderBottom: 'none' } }}>
      <MalLink anime={anime} sx={{ flexShrink: 0, display: 'block' }}>
        <Poster anime={anime} />
      </MalLink>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <MalLink anime={anime}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {animeTitle(anime)}
          </Typography>
        </MalLink>
        <AnimeMeta anime={anime} />
      </Box>

      <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: { xs: 88, sm: 124 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{anime.votes || 0}</Typography>
        <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 750 }}>{plural(anime.votes || 0, 'vote')}</Typography>
        <Box sx={{ mt: 0.75 }}>
          <Button
            size="small"
            variant={voted ? 'outlined' : 'contained'}
            disabled={disabled || loadingId === anime.mal_id}
            onClick={() => onToggleVote(anime)}
            startIcon={loadingId === anime.mal_id ? <CircularProgress size={13} /> : voted ? <UndoIcon /> : <HowToVoteIcon />}
            sx={{ minWidth: 90, borderRadius: 999, textTransform: 'none', fontWeight: 850, boxShadow: 'none', backgroundColor: voted ? '#fff' : '#111827', color: voted ? '#111827' : '#fff', borderColor: '#d4d4d8', '&:hover': { boxShadow: 'none', backgroundColor: voted ? '#fafafa' : '#000' } }}
          >
            {voted ? 'Un-vote' : 'Vote'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function SearchRow({ anime, onAddClick }) {
  const episodeCount = getEpisodeCount(anime);
  const tooLong = episodeCount != null && episodeCount > MAX_EPISODES;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.25, sm: 1.75 }, py: 1, minHeight: 88, borderBottom: '1px solid #eef2f7', '&:last-of-type': { borderBottom: 'none' } }}>
      <Poster anime={anime} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{animeTitle(anime)}</Typography>
        <AnimeMeta anime={anime} />
      </Box>
      <Button size="small" variant="outlined" onClick={() => onAddClick(anime)} sx={{ flexShrink: 0, borderRadius: 999, textTransform: 'none', fontWeight: 850, color: tooLong ? '#b91c1c' : '#111827', borderColor: tooLong ? '#fecaca' : '#d4d4d8', backgroundColor: tooLong ? '#fff5f5' : '#fff' }}>
        Add
      </Button>
    </Box>
  );
}

function ArchivePoll({ archive }) {
  const [open, setOpen] = useState(false);
  const items = sortItems(archive.items || []);
  const winner = items[0];
  return (
    <Box sx={{ borderTop: '1px solid #eef2f7' }}>
      <Button fullWidth onClick={() => setOpen((value) => !value)} sx={{ justifyContent: 'space-between', textTransform: 'none', color: '#111827', px: { xs: 1.25, sm: 1.75 }, py: 1.25, borderRadius: 0 }}>
        <Box sx={{ textAlign: 'left', minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Poll ending {formatSydneyDate(archive.end_at)}</Typography>
          <Typography variant="caption" sx={{ color: '#71717a' }}>{winner ? `Winner: ${animeTitle(winner)} (${winner.votes || 0} ${plural(winner.votes || 0, 'vote')})` : 'No entries'}</Typography>
        </Box>
        <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </Button>
      {open ? (
        <Box>
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ px: 2, py: 1.5, color: '#71717a' }}>This poll closed with no anime submitted.</Typography>
          ) : items.map((anime, index) => (
            <Box key={anime.mal_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: 1.25, sm: 1.75 }, py: 0.8, minHeight: 76, borderTop: index === 0 ? 'none' : '1px solid #eef2f7', backgroundColor: '#fbfdff' }}>
              <Typography variant="caption" sx={{ width: 24, color: '#71717a', fontWeight: 900 }}>#{index + 1}</Typography>
              <Poster anime={anime} compact />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{animeTitle(anime)}</Typography>
                <AnimeMeta anime={anime} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 900, flexShrink: 0 }}>{anime.votes || 0} {plural(anime.votes || 0, 'vote')}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

const WeeklyPoll = ({ baseUrl = 'https://eliteari.pythonanywhere.com' }) => {
  const [pollData, setPollData] = useState(null);
  const [loadingPoll, setLoadingPoll] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [confirmAnime, setConfirmAnime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [voteLoadingId, setVoteLoadingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [archivesOpen, setArchivesOpen] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const serverClockRef = useRef({ serverNowMs: Date.now(), clientFetchedMs: Date.now() });

  const apiBaseUrl = baseUrl.replace(/\/$/, '');

  const loadPoll = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingPoll(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/weekly/current`);
      setPollData(response.data);
      const serverNow = response.data?.timing?.server_now;
      if (serverNow) {
        const serverNowMs = new Date(serverNow).getTime();
        if (!Number.isNaN(serverNowMs)) {
          serverClockRef.current = { serverNowMs, clientFetchedMs: Date.now() };
          setNowMs(serverNowMs);
        }
      }
    } catch (error) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || 'Could not load the poll. Please try again soon.' });
    } finally {
      if (!silent) setLoadingPoll(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => { loadPoll(); }, [loadPoll]);

  useEffect(() => {
    const interval = setInterval(() => {
      const { serverNowMs, clientFetchedMs } = serverClockRef.current;
      setNowMs(serverNowMs + (Date.now() - clientFetchedMs));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const target = pollData?.timing?.countdown_target;
    if (!target) return undefined;
    const targetMs = new Date(target).getTime();
    if (!Number.isNaN(targetMs) && targetMs - nowMs <= 0) {
      const timeout = setTimeout(() => loadPoll({ silent: true }), 800);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [loadPoll, nowMs, pollData?.timing?.countdown_target]);

  const isPollOpen = pollData?.status === 'open';
  const limits = pollData?.limits || pollData?.submission_quota || {};
  const pollItems = useMemo(() => sortItems(pollData?.poll?.items || []), [pollData?.poll?.items]);
  const archives = pollData?.archives || [];
  const parts = countdownParts(new Date(pollData?.timing?.countdown_target || Date.now()).getTime() - nowMs);
  const confirmTooLong = Number.isFinite(confirmAnime?.episodes) && confirmAnime.episodes > MAX_EPISODES;
  const limit = Number(limits?.max_suggestions_per_ip ?? limits?.limit ?? 6);
  const used = Number(limits?.suggestions_used_by_you ?? limits?.used ?? 0);
  const limitReached = Number.isFinite(limit) && Number.isFinite(used) && used >= limit;

  const searchAnime = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    setMessage(null);
    setSearched(false);
    setResults([]);
    if (!trimmed) return;
    if (!isPollOpen) {
      setMessage({ severity: 'info', text: 'The poll is closed right now. Search again when the next poll opens.' });
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/weekly/search`, { params: { query: trimmed } });
      setResults(response.data?.results || []);
    } catch (error) {
      setMessage({ severity: error?.response?.status === 429 ? 'warning' : 'error', text: error?.response?.data?.message || 'Search failed. Please try again.' });
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const submitAnime = async () => {
    if (!confirmAnime) return;
    if (limitReached) {
      setMessage({ severity: 'warning', text: `You can only suggest up to ${Number.isFinite(limit) ? limit : 6} anime for this poll and can't submit any more.` });
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/weekly/add`, { mal_id: confirmAnime.mal_id });
      setPollData(response.data);
      setMessage({ severity: 'success', text: `${animeTitle(confirmAnime)} was added to the poll.` });
      setConfirmAnime(null);
      setQuery('');
      setResults([]);
      setSearched(false);
    } catch (error) {
      setMessage({ severity: error?.response?.status === 403 ? 'warning' : error?.response?.status === 409 ? 'info' : 'error', text: error?.response?.data?.message || 'Could not submit that anime. Please try again.' });
    } finally {
      setSubmitting(false);
      loadPoll({ silent: true });
    }
  };

  const toggleVote = async (anime) => {
    const unvote = Boolean(anime.voted_by_you);
    setVoteLoadingId(anime.mal_id);
    setMessage(null);
    try {
      const response = await axios.post(`${apiBaseUrl}/weekly/${unvote ? 'unvote' : 'vote'}`, { mal_id: anime.mal_id });
      setPollData(response.data);
      // Intentionally no success banner here: vote/un-vote should not move the poll list down.
    } catch (error) {
      setMessage({ severity: error?.response?.status === 409 ? 'info' : 'error', text: error?.response?.data?.message || 'Could not update your vote. Please try again.' });
      loadPoll({ silent: true });
    } finally {
      setVoteLoadingId(null);
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 52%)' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2.5, sm: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Typography variant="overline" sx={{ color: '#71717a', fontWeight: 900, letterSpacing: 1.2 }}>Anime shortlist</Typography>
          <Typography variant="h4" sx={{ fontWeight: 950, color: '#111827', letterSpacing: '-0.04em' }}>Fortnightly Poll</Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: '#71717a' }}>Suggest and vote for short anime for Ari to watch.</Typography>
        </Box>

        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 4, border: '1px solid #e5e7eb', backgroundColor: '#fff', boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)' }}>
          {loadingPoll && !pollData ? (
            <Box sx={{ py: 3 }}><LinearProgress sx={{ borderRadius: 999 }} /><Typography variant="body2" sx={{ mt: 1.5, color: '#71717a', textAlign: 'center' }}>Loading poll...</Typography></Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap">
                <CountdownTile label="days" value={parts.days} />
                <CountdownTile label="hours" value={parts.hours} />
                <CountdownTile label="mins" value={parts.minutes} />
                <CountdownTile label="secs" value={parts.seconds} />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', color: '#71717a'}}>
                {isPollOpen ? 'Poll closes' : 'Next poll opens'} {formatSydneyDate(pollData?.timing?.countdown_target)} Sydney time.
              </Typography>
              {!isPollOpen ? <Alert severity="info" sx={{ mt: 1.5, borderRadius: 3 }}>The poll is currently closed. Final results are available in the archive below.</Alert> : null}
            </>
          )}
        </Paper>

        {message ? <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2, borderRadius: 3 }}>{message.text}</Alert> : null}

        <Paper elevation={0} sx={{ p: { xs: 1.25, sm: 1.6 }, mb: 2, borderRadius: 4, border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.1, sm: 0.75 }} alignItems="center" justifyContent="center" sx={{ mb: 1.25, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Add an anime</Typography>
            <Typography variant="body2" sx={{ color: '#71717a', fontWeight: 750 }}>(up to 24 episodes long!)</Typography>
          </Stack>
          <form onSubmit={searchAnime}>
            <Paper elevation={0} sx={{ p: '5px 6px', display: 'flex', alignItems: 'center', borderRadius: 999, border: '1px solid #e4e4e7', backgroundColor: isPollOpen ? '#fff' : '#f4f4f5' }}>
              <InputBase sx={{ ml: 1.5, flex: 1 }} placeholder={isPollOpen ? 'Search Jikan for an anime...' : 'Poll is closed'} value={query} disabled={!isPollOpen || searching} onChange={(event) => setQuery(event.target.value)} />
              {query ? <IconButton type="button" onClick={() => { setQuery(''); setResults([]); setSearched(false); }} disabled={searching}><CloseIcon /></IconButton> : null}
              <IconButton type="submit" disabled={!isPollOpen || searching}>{searching ? <CircularProgress size={21} /> : <SearchIcon />}</IconButton>
            </Paper>
          </form>
        </Paper>

        {!searching && results.length > 0 ? <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 4, border: '1px solid #e5e7eb', overflow: 'hidden' }}>{results.map((anime) => <SearchRow key={anime.mal_id} anime={anime} onAddClick={setConfirmAnime} />)}</Paper> : null}
        {!searching && searched && results.length === 0 ? <Typography variant="body2" sx={{ mb: 2.5, textAlign: 'center', color: '#71717a' }}>No anime found. Try another search.</Typography> : null}

        <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid #e5e7eb', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 16px 40px rgba(15, 23, 42, 0.05)' }}>
          <Box sx={{ px: { xs: 1.25, sm: 1.75 }, py: 1.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Current poll</Typography>
              <Typography variant="caption" sx={{ color: '#71717a'}}>{pollItems.length} anime submitted</Typography>
            </Box>
            {loadingPoll ? <CircularProgress size={20} /> : null}
          </Box>
          <Divider />
          {pollItems.length === 0 ? <Typography variant="body2" sx={{ px: 2, py: 3, color: '#71717a', textAlign: 'center' }}>No anime have been added to this poll yet.</Typography> : pollItems.map((anime) => <PollRow key={anime.mal_id} anime={anime} onToggleVote={toggleVote} loadingId={voteLoadingId} disabled={!isPollOpen} />)}
        </Paper>

        <Paper elevation={0} sx={{ mt: 2.5, borderRadius: 4, border: '1px solid #e5e7eb', overflow: 'hidden', backgroundColor: '#fff' }}>
          <Button fullWidth onClick={() => setArchivesOpen((value) => !value)} sx={{ justifyContent: 'space-between', textTransform: 'none', px: { xs: 1.25, sm: 1.75 }, py: 1.4, color: '#111827' }}>
            <Stack direction="row" spacing={1} alignItems="center"><ArchiveIcon fontSize="small" /><Box sx={{ textAlign: 'left' }}><Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Archived results</Typography><Typography variant="caption" sx={{ color: '#71717a' }}>{archives.length} previous {plural(archives.length, 'poll')}</Typography></Box></Stack>
            <ExpandMoreIcon sx={{ transform: archivesOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </Button>
          {archivesOpen ? (
            <Box>
              {archives.length === 0 ? <Typography variant="body2" sx={{ px: 2, py: 1.5, color: '#71717a', borderTop: '1px solid #eef2f7' }}>No archived polls yet.</Typography> : archives.map((archive) => <ArchivePoll key={archive.poll_id} archive={archive} />)}
            </Box>
          ) : null}
        </Paper>
      </Container>

      {confirmAnime ? (
          <Dialog open={Boolean(confirmAnime)} onClose={() => setConfirmAnime(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 900 }}>{confirmTooLong ? 'This anime is too long...' : 'Ready to submit?'}</DialogTitle>
            <DialogContent>
              {confirmTooLong ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}><strong>{animeTitle(confirmAnime)}</strong> has {confirmAnime?.episodes} episodes. The poll only accepts anime up to {MAX_EPISODES} episodes long.</Alert>
              ) : (
                <>
                  <Typography variant="body1">Ready to submit <strong>{animeTitle(confirmAnime)}</strong>?</Typography>
                  <QuotaBar limits={limits} />
                  {limitReached ? <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>You can only suggest up to {Number.isFinite(limit) ? limit : 6} anime for this poll and can't submit any more.</Alert> : null}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              {confirmTooLong ? (
                <Button onClick={() => setConfirmAnime(null)} variant="contained" sx={{ borderRadius: 999, backgroundColor: '#111827', boxShadow: 'none', '&:hover': { backgroundColor: '#000' } }}>Go Back</Button>
              ) : (
                <>
                  <Button onClick={() => setConfirmAnime(null)} disabled={submitting} sx={{ borderRadius: 999 }}>Cancel</Button>
                  <Button onClick={submitAnime} disabled={submitting || limitReached} variant="contained" sx={{ borderRadius: 999, backgroundColor: '#111827', boxShadow: 'none', '&:hover': { backgroundColor: '#000' } }}>{submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit'}</Button>
                </>
              )}
            </DialogActions>
          </Dialog>
        ) : null}
    </Box>
  );
};

export default WeeklyPoll;
